<template>
  <div class="map-container">
    <div id="map" class="map"></div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore"
import type { IGeoJsonFeature, IGeoJsonProps, IGeoJsonFeatureCollection } from "@features/Pages/TimelineMapPage/_js/app/interfaces/IGeoJsonFeature"
import { initMap, destroyMap } from "@features/Pages/TimelineMapPage/_js/app/services/vrak/initMap"

const store = useTimelineMapStore()

const timelineMapItems = computed(() =>
  store.epochs.flatMap(epoch =>
    epoch.timelineMapItems
      ? epoch.timelineMapItems.filter(item => item.type === "Moment")
      : []
  )
)

const featureCollection = computed<IGeoJsonFeatureCollection>(() => {
  const features: IGeoJsonFeature[] = []
  for (const item of timelineMapItems.value) {
    if (item?.type === "Moment" && item.longitude != null && item.latitude != null) {
      const lng = Number(item.longitude)
      const lat = Number(item.latitude)
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) { continue }

      features.push({
        id: String(item.id),
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lng, lat] as [number, number]
        },
        properties: {
          id: item.id,
          year: item.year,
          title: item.title,
          excerpt: item.momentExcerpt,
          imageHtml: item.imageHtml
        } satisfies IGeoJsonProps
      })
    }
  }
  return { type: 'FeatureCollection', features } satisfies IGeoJsonFeatureCollection
})

let lastHash = ''
const hashFc = (fc: IGeoJsonFeatureCollection) => JSON.stringify(fc)

function initOrUpdateMap(): void {
  const fc = featureCollection.value
  const next = hashFc(fc)
  if (next === lastHash) { return }
  lastHash = next
  void initMap('map', fc)
}

onMounted(() => {
  initOrUpdateMap()
})

watch(featureCollection, () => {
  initOrUpdateMap()
})

onBeforeUnmount(() => {
  destroyMap()
})
</script>
